/**
 * useConsoleCapture Hook
 * 
 * Intercepts console methods (log, warn, error, info) to capture console output
 * for debugging and bug reporting purposes. Maintains a rolling buffer of the
 * last 100 console entries with timestamps and preserves original functionality.
 * 
 * @author American Police Training
 * @version 1.0.0
 */

import { useEffect } from 'react';
import { logger } from '../utils/logger';
import {
  initConsoleCapture,
  getCapturedConsoleLogs,
  clearCapturedConsoleLogs,
  ConsoleLogEntry
} from '../utils/consoleCapture';

interface ConsoleInterceptor {
  getCapturedLogs: () => ConsoleLogEntry[];
  clearCapturedLogs: () => void;
}

/**
 * Custom hook to intercept and capture console output
 * 
 * @param maxEntries - Maximum number of console entries to store (default: 100)
 * @returns Object with methods to retrieve and clear captured logs
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { getCapturedLogs, clearCapturedLogs } = useConsoleCapture();
 *   
 *   const handleSubmitBug = () => {
 *     const logs = getCapturedLogs();
 *     logger.debug('Captured logs:', logs);
 *     clearCapturedLogs();
 *   };
 * }
 * ```
 */
export const useConsoleCapture = (maxEntries: number = 100): ConsoleInterceptor => {
  useEffect(() => {
    initConsoleCapture(maxEntries);
  }, [maxEntries]);

  /**
   * Get all captured console logs
   */
  const getCapturedLogs = (): ConsoleLogEntry[] => {
    return getCapturedConsoleLogs(maxEntries);
  };

  /**
   * Clear all captured console logs
   */
  return {
    getCapturedLogs,
    clearCapturedLogs: clearCapturedConsoleLogs,
  };
};

export default useConsoleCapture;

